import { create, fromBinary, toBinary, toJson } from '@bufbuild/protobuf';
import type { DescMessage, MessageInitShape, MessageShape } from '@bufbuild/protobuf';
import { getGrpcBaseUrl } from './transport';
import { webLog } from './webLogger';
import { makeTraceparent } from '../traceparent';
import {
  CORRELATION_ID_HEADER,
  getRecordingSink,
  newCorrelationId,
  recordRawUnaryCall,
} from '../../../testRecords/recording';

export type ParsedGrpcWebResponse = {
  messageBytes?: Uint8Array;
  grpcStatus?: string;
  grpcMessage?: string;
  trailers?: string;
};

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs?: number
): Promise<Response> {
  if (timeoutMs === undefined) {
    return fetch(input, init);
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Buffered gRPC-web unary call via raw fetch + response.arrayBuffer().
 *
 * The Revit plugin WebView2 fails connect-es's STREAMING response reader
 * (response.body.getReader()) with "Element not found (0x80070490)" even though
 * the HTTP request itself returns 200 OK with the full gRPC-web body (confirmed
 * via the WebView2 Network tab + a plain curl). Reading the whole body at once
 * with arrayBuffer() sidesteps the broken streaming path. Shared by the
 * validation and plugin-update unary reads so they stop wedging the page on a
 * transport error.
 */
export async function unaryViaDirectGrpcWeb<Req extends DescMessage, Res extends DescMessage>(
  path: string,
  requestSchema: Req,
  responseSchema: Res,
  requestInit: MessageInitShape<Req>,
  correlationTraceId?: string,
  timeoutMs?: number
): Promise<MessageShape<Res>> {
  const method = path.slice(path.lastIndexOf('/') + 1);
  const baseUrl = getGrpcBaseUrl();
  if (!baseUrl || typeof fetch !== 'function') {
    webLog.error('directGrpcWeb', `grpc-web ${method}: transport unavailable (baseUrl=${baseUrl ?? 'null'})`);
    throw new Error('gRPC transport is unavailable');
  }

  // Stamp a W3C traceparent so the plugin-side AspNetCore instrumentation
  // opens a server span under the SAME trace id we log here. The raw fetch
  // bypasses connect-es interceptors, so we must add the header by hand or
  // the request would be invisible in Tempo. The trace id is echoed into
  // every breadcrumb below so Loki <-> Tempo correlate in one click.
  const traceparent = makeTraceparent(correlationTraceId);
  const traceId = traceparent.split('-')[1] ?? 'unknown';
  const startedAt = typeof performance !== 'undefined' ? performance.now() : 0;

  // Test-Records recorder: the raw-fetch path bypasses connect-es interceptors,
  // so capture is wired here by hand. Zero-overhead and behaviour-neutral when
  // no recording session is active (default inactive sink).
  const recording = getRecordingSink().isRecording();
  const correlationId = recording ? newCorrelationId() : '';
  const recStartIso = recording ? new Date().toISOString() : '';

  const request = create(requestSchema, requestInit);
  const requestBytes = toBinary(requestSchema, request);
  webLog.info(
    'directGrpcWeb',
    `grpc-web ${method}: -> POST ${path} trace=${traceId} reqBytes=${requestBytes.length}`
  );

  const headers: Record<string, string> = {
    'Content-Type': 'application/grpc-web+proto',
    'X-Grpc-Web': '1',
    'X-User-Agent': 'pipeex-web',
    traceparent,
  };
  if (recording) {
    headers[CORRELATION_ID_HEADER] = correlationId;
  }

  const doCall = async (): Promise<MessageShape<Res>> => {
    let response: Response;
    try {
      response = await fetchWithTimeout(`${baseUrl}${path}`, {
        method: 'POST',
        headers,
        body: new Blob([toArrayBuffer(frameGrpcWebMessage(requestBytes))], {
          type: 'application/grpc-web+proto',
        }),
      }, timeoutMs);
    } catch (fetchError) {
      const took = startedAt ? Math.round(performance.now() - startedAt) : -1;
      const message = fetchError instanceof Error ? fetchError.message : String(fetchError);
      webLog.error(
        'directGrpcWeb',
        `grpc-web ${method}: <- network-error trace=${traceId} tookMs=${took} ${message}`
      );
      throw fetchError;
    }

    const responseBytes = new Uint8Array(await response.arrayBuffer());
    const parsed = parseGrpcWebResponse(responseBytes);
    const took = startedAt ? Math.round(performance.now() - startedAt) : -1;
    webLog.info(
      'directGrpcWeb',
      `grpc-web ${method}: <- http=${response.status} grpcStatus=${parsed.grpcStatus ?? '?'} ` +
        `respBytes=${responseBytes.length} dataBytes=${parsed.messageBytes?.length ?? 0} ` +
        `trace=${traceId} tookMs=${took}`
    );

    if (!response.ok) {
      webLog.error(
        'directGrpcWeb',
        `grpc-web ${method}: <- http-error ${response.status} trace=${traceId} msg=${parsed.grpcMessage ?? ''}`
      );
      throw new Error(parsed.grpcMessage || `HTTP ${response.status}`);
    }
    if (parsed.grpcStatus && parsed.grpcStatus !== '0') {
      webLog.error(
        'directGrpcWeb',
        `grpc-web ${method}: <- grpc-error status=${parsed.grpcStatus} trace=${traceId} msg=${parsed.grpcMessage ?? ''}`
      );
      throw new Error(parsed.grpcMessage || `gRPC status ${parsed.grpcStatus}`);
    }
    if (!parsed.messageBytes) {
      webLog.error(
        'directGrpcWeb',
        `grpc-web ${method}: <- empty-body trace=${traceId} (no data frame in gRPC-web response)`
      );
      throw new Error('gRPC-web response did not contain a data frame');
    }
    return fromBinary(responseSchema, parsed.messageBytes);
  };

  if (!recording) {
    return doCall();
  }

  const service = path.slice(1, path.lastIndexOf('/'));
  const recPerfStart = performance.now();
  try {
    const decoded = await doCall();
    recordRawUnaryCall({
      service,
      methodName: method,
      correlationId,
      startedAt: recStartIso,
      endedAt: new Date().toISOString(),
      durationMs: Math.round((performance.now() - recPerfStart) * 10) / 10,
      status: 'OK',
      requestJson: JSON.stringify(toJson(requestSchema, request)),
      responseJson: JSON.stringify(toJson(responseSchema, decoded)),
    });
    return decoded;
  } catch (error) {
    recordRawUnaryCall({
      service,
      methodName: method,
      correlationId,
      startedAt: recStartIso,
      endedAt: new Date().toISOString(),
      durationMs: Math.round((performance.now() - recPerfStart) * 10) / 10,
      status: 'ERROR',
      error: error instanceof Error ? error.message : String(error),
      requestJson: JSON.stringify(toJson(requestSchema, request)),
    });
    throw error;
  }
}

export function frameGrpcWebMessage(messageBytes: Uint8Array): Uint8Array {
  const frame = new Uint8Array(messageBytes.length + 5);
  frame[0] = 0;
  frame[1] = (messageBytes.length >>> 24) & 0xff;
  frame[2] = (messageBytes.length >>> 16) & 0xff;
  frame[3] = (messageBytes.length >>> 8) & 0xff;
  frame[4] = messageBytes.length & 0xff;
  frame.set(messageBytes, 5);
  return frame;
}

export function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

export function parseGrpcWebResponse(bytes: Uint8Array): ParsedGrpcWebResponse {
  let offset = 0;
  const result: ParsedGrpcWebResponse = {};
  const decoder = new TextDecoder();

  while (offset + 5 <= bytes.length) {
    const flags = bytes[offset];
    const length =
      ((bytes[offset + 1] << 24) >>> 0) |
      (bytes[offset + 2] << 16) |
      (bytes[offset + 3] << 8) |
      bytes[offset + 4];
    const start = offset + 5;
    const end = start + length;
    if (end > bytes.length) {
      break;
    }

    const payload = bytes.subarray(start, end);
    if ((flags & 0x80) === 0x80) {
      result.trailers = decoder.decode(payload);
      applyGrpcTrailers(result, result.trailers);
    } else if (!result.messageBytes) {
      result.messageBytes = payload;
    }

    offset = end;
  }

  return result;
}

function applyGrpcTrailers(result: ParsedGrpcWebResponse, trailers: string): void {
  trailers.split(/\r?\n/).forEach((line) => {
    const separatorIndex = line.indexOf(':');
    if (separatorIndex <= 0) {
      return;
    }

    const key = line.slice(0, separatorIndex).trim().toLowerCase();
    const value = line.slice(separatorIndex + 1).trim();
    if (key === 'grpc-status') {
      result.grpcStatus = value;
    } else if (key === 'grpc-message') {
      result.grpcMessage = decodeGrpcMessage(value);
    }
  });
}

function decodeGrpcMessage(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
